// SkipEnquiryFunnelPage — /reports/skip-management/enquiries.
//
// Enquiry pipeline + conversion performance. KPI cards + two bar charts
// (enquiries by status, contact outcomes) + a sortable per-status table.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { DateRangeFilter, lastThirtyDays } from './components/DateRangeFilter';
import { KpiGrid, type KpiTile } from './components/KpiGrid';
import { BarChart } from './components/BarChart';
import { SortableTable, type SortableColumn } from './components/SortableTable';
import { formatNumber, pct, days } from './components/format';
import { useEnquiryFunnel, type DateRange } from './useSkipReports';
import type { ReportCell } from '../useReports';
import styles from './skipReports.module.css';

const TABLE_COLUMNS: SortableColumn[] = [
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'enquiries', label: 'Enquiries', type: 'number' },
  { key: 'est_value', label: 'Estimated value', type: 'currency' },
  { key: 'avg_contact_attempts', label: 'Avg contact attempts', type: 'number' },
];

export function SkipEnquiryFunnelPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<DateRange>(lastThirtyDays());
  const [submitted, setSubmitted] = useState<DateRange>(form);
  const enabled = !!submitted.date_from && !!submitted.date_to;

  const query = useEnquiryFunnel(submitted, enabled);
  const data = query.data ?? null;

  const kpis = useMemo<KpiTile[]>(() => {
    if (!data) return [];
    return [
      { label: 'Total enquiries', value: formatNumber(data.kpis.total_enquiries), tone: 'blue' },
      { label: 'Converted', value: formatNumber(data.kpis.converted), tone: 'green' },
      { label: 'Conversion rate', value: pct(data.kpis.conversion_rate), tone: 'teal' },
      { label: 'Avg time to convert', value: days(data.kpis.avg_days_to_convert), tone: 'amber' },
    ];
  }, [data]);

  const tableRows = useMemo<Record<string, ReportCell>[]>(
    () =>
      (data?.by_status ?? []).map((r) => ({
        status: r.status,
        enquiries: r.enquiries,
        est_value: r.est_value,
        avg_contact_attempts: r.avg_contact_attempts,
      })),
    [data],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Lead Conversion"
        subtitle="Enquiry funnel and conversion performance over a date range"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/reports/skip-management')}>
            Back
          </Button>
        }
      />

      <DateRangeFilter
        form={form}
        onChange={setForm}
        onSubmit={() => setSubmitted({ ...form })}
        loading={query.isFetching}
      />

      {query.isFetching && !data ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {data ? <KpiGrid tiles={kpis} /> : null}

      {data ? (
        <Card title="Enquiries by status">
          <BarChart
            data={data.by_status.map((r) => ({ label: r.status, value: r.enquiries }))}
            colour="var(--color-secondary, #2563eb)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Contact outcomes">
          <BarChart
            data={data.contact_outcomes.map((r) => ({ label: r.outcome, value: r.attempts }))}
            colour="var(--color-status-awaiting_text, #0369a1)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Status breakdown" flush>
          <SortableTable columns={TABLE_COLUMNS} rows={tableRows} exportName="skip-enquiry-funnel" />
        </Card>
      ) : null}

      {!query.isFetching && !data ? (
        <Card>
          <p className={styles.empty}>
            <TrendingUp size={20} aria-hidden /> Pick a date range and refresh to see the report.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
